import { Trans } from "@lingui/react";
import { WIcon } from "@mobility-item-page/client";
import { Button } from "@warp-ds/elements/react/button";
import DOMPurify from "isomorphic-dompurify";
import { useEffect, useRef, useState } from "react";
import { ScreenReaderSeparator } from "./ScreenReaderSeparator.tsx";

export const Description = ({
  className = "",
  trackClick,
  description,
}: {
  className?: string;
  trackClick: () => void;
  description: string | undefined;
}) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [canExpand, setCanExpand] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded((prev) => {
      if (!prev) {
        trackClick();
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (descriptionRef.current && descriptionRef.current.getBoundingClientRect().height > 110) {
      setCanExpand(true);
    } else {
      setCanExpand(false);
    }
  }, []);

  if (!description) {
    return null;
  }

  const sanitizedDescription = DOMPurify.sanitize(description);

  return (
    <div className={`${className}`}>
      <div
        ref={descriptionRef}
        aria-describedby={canExpand ? "dealer-description" : undefined}
        className={`relative whitespace-pre-line ${expanded || !canExpand ? "max-h-none" : "max-h-[110px] overflow-hidden"}`}
      >
        {!expanded && canExpand && (
          <div
            className="absolute top-0 h-full w-full pointer-events-none [--fade-color:var(--w-s-color-background-info-subtle)] dark:[--fade-color:var(--w-s-color-background-subtle)]"
            style={{ background: "linear-gradient(to top, var(--fade-color), transparent)" }}
          />
        )}
        {sanitizedDescription}
      </div>
      {canExpand && (
        <div className={`relative s-bg-info-subtle dark:s-bg-subtle`}>
          <Button onClick={toggleExpanded} variant="quiet" aria-expanded={expanded} aria-controls="dealer-description">
            {expanded ? (
              <span className="flex items-center gap-8">
                <WIcon name="Minus" />
                <ScreenReaderSeparator />
                <Trans id="dealer.description.showLess" message="Show less" />
              </span>
            ) : (
              <span className="flex items-center gap-8">
                <WIcon name="Plus" />
                <ScreenReaderSeparator />
                <Trans id="dealer.description.showDescription" message="Show entire description" />
              </span>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
